import React, { useState, useEffect, useRef } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { LLM_PROVIDER_LOGOS, LlmProvider } from '@/types/llm-provider';

interface LLMStep {
  provider: LlmProvider;
  name: string;
  logo: string;
}

const LLM_STEPS: LLMStep[] = [
  { provider: LlmProvider.ChatGPT, name: 'ChatGPT', logo: LLM_PROVIDER_LOGOS[LlmProvider.ChatGPT] },
  { provider: LlmProvider.Claude, name: 'Claude', logo: LLM_PROVIDER_LOGOS[LlmProvider.Claude] },
  { provider: LlmProvider.Gemini, name: 'Gemini', logo: LLM_PROVIDER_LOGOS[LlmProvider.Gemini] },
  { provider: LlmProvider.Perplexity, name: 'Perplexity', logo: LLM_PROVIDER_LOGOS[LlmProvider.Perplexity] },
  { provider: LlmProvider.Grok, name: 'Grok', logo: LLM_PROVIDER_LOGOS[LlmProvider.Grok] },
  { provider: LlmProvider.DeepSeek, name: 'DeepSeek', logo: LLM_PROVIDER_LOGOS[LlmProvider.DeepSeek] },
];

type SubStep = 'waiting' | 'querying' | 'analyzing' | 'completed';

const SUB_STEP_LABELS: Record<SubStep, string> = {
  waiting: 'Waiting',
  querying: 'Sending query',
  analyzing: 'Analyzing response',
  completed: 'Completed',
};

const AnalysisProgress: React.FC = () => {
  const [currentLLMIndex, setCurrentLLMIndex] = useState(0);
  const [currentSubStep, setCurrentSubStep] = useState<SubStep>('querying');
  const [completedLLMs, setCompletedLLMs] = useState<Set<number>>(new Set());
  const [isCompilingReport, setIsCompilingReport] = useState(false);
  const subStepIndexRef = useRef(0);

  useEffect(() => {
    const subStepOrder: SubStep[] = ['querying', 'analyzing', 'completed'];

    const interval = setInterval(() => {
      const nextSubStepIndex = subStepIndexRef.current + 1;

      if (nextSubStepIndex >= subStepOrder.length) {
        // Mark current LLM as completed and move to next
        setCompletedLLMs((prev) => new Set([...prev, currentLLMIndex]));

        if (currentLLMIndex < LLM_STEPS.length - 1) {
          setCurrentLLMIndex((prev) => prev + 1);
          subStepIndexRef.current = 0;
          setCurrentSubStep('querying');
        } else {
          // All LLMs done, show compiling report
          setIsCompilingReport(true);
        }
      } else {
        subStepIndexRef.current = nextSubStepIndex;
        setCurrentSubStep(subStepOrder[nextSubStepIndex]);
      }
    }, 1200);

    return () => clearInterval(interval);
  }, [currentLLMIndex]);

  const getLLMStatus = (index: number): SubStep => {
    if (completedLLMs.has(index)) return 'completed';
    if (index === currentLLMIndex) return currentSubStep;
    return 'waiting';
  };

  return (
    <div className="h-screen flex flex-col items-center pt-10 px-6 overflow-hidden">
      <div className="w-full max-w-xl">
        <div className="text-center mb-6">
          <h1 className="font-condensed font-bold text-3xl uppercase mb-2">
            Analyzing Your Presence
          </h1>
          <p className="font-sans text-base text-muted-foreground">
            Querying multiple AI models to see what they say about you
          </p>
          <p className="font-sans text-xs text-muted-foreground/60 mt-1.5">
            This may take up to 60 seconds
          </p>
        </div>

        <div className="mb-4 flex justify-center">
          <div className="flex items-center gap-2.5">
            <div className="flex gap-1">
              {LLM_STEPS.map((_, index) => (
                <div
                  key={index}
                  className={`
                    w-2 h-2 rounded-full transition-all duration-300
                    ${completedLLMs.has(index) || isCompilingReport ? 'bg-primary' : index === currentLLMIndex ? 'bg-primary animate-pulse' : 'bg-border'}
                  `}
                />
              ))}
              <div
                className={`
                  w-2 h-2 rounded-full transition-all duration-300
                  ${isCompilingReport ? 'bg-primary animate-pulse' : 'bg-border'}
                `}
              />
            </div>
            <span className="text-xs text-muted-foreground">
              {isCompilingReport ? 'Finalizing...' : `${completedLLMs.size} of ${LLM_STEPS.length} completed`}
            </span>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-5">
          <div className="space-y-2.5">
            {LLM_STEPS.map((llm, index) => {
              const status = getLLMStatus(index);
              const isActive = index === currentLLMIndex && status !== 'completed' && !isCompilingReport;
              const isCompleted = status === 'completed' || isCompilingReport;
              const isWaiting = status === 'waiting';

              return (
                <div
                  key={llm.provider}
                  className={`
                    flex items-center gap-4 p-3 rounded-lg transition-all duration-300
                    ${isActive ? 'bg-primary/5 border border-primary/20' : ''}
                    ${isCompleted ? 'bg-muted/30' : ''}
                    ${isWaiting ? 'opacity-40' : ''}
                  `}
                >
                  <div className="relative">
                    <div className={`
                      w-11 h-11 rounded-full bg-white border-2 flex items-center justify-center overflow-hidden
                      ${isActive ? 'border-primary' : 'border-border'}
                      ${isCompleted ? 'border-primary' : ''}
                    `}>
                      <img
                        src={llm.logo}
                        alt={llm.name}
                        className="w-7 h-7 object-contain"
                      />
                    </div>
                    {isCompleted && (
                      <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-base ${isWaiting ? 'text-muted-foreground' : 'text-foreground'}`}>
                      {llm.name}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {isActive && (
                        <Loader2 className="w-3.5 h-3.5 text-primary animate-spin" />
                      )}
                      <p className={`text-sm ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                        {isCompleted ? SUB_STEP_LABELS['completed'] : SUB_STEP_LABELS[status]}
                      </p>
                    </div>
                  </div>

                  {isActive && (
                    <div className="flex gap-1">
                      {['querying', 'analyzing', 'completed'].map((step) => {
                        const subStepOrder = ['querying', 'analyzing', 'completed'];
                        const currentSubIndex = subStepOrder.indexOf(currentSubStep);
                        const stepIndex = subStepOrder.indexOf(step);

                        return (
                          <div
                            key={step}
                            className={`
                              w-2 h-2 rounded-full transition-all duration-300
                              ${stepIndex <= currentSubIndex ? 'bg-primary' : 'bg-border'}
                            `}
                          />
                        );
                      })}
                    </div>
                  )}

                  {isCompleted && (
                    <Check className="w-5 h-5 text-primary" strokeWidth={2.5} />
                  )}
                </div>
              );
            })}

            {/* Compiling Report Step */}
            <div
              className={`
                flex items-center gap-4 p-3 rounded-lg transition-all duration-300 border-2 border-dashed
                ${isCompilingReport ? 'bg-primary/5 border-primary/30' : 'opacity-40 border-border'}
              `}
            >
              <div className={`
                w-11 h-11 rounded-full flex items-center justify-center
                ${isCompilingReport ? 'bg-primary/10' : 'bg-muted'}
              `}>
                {isCompilingReport ? (
                  <Loader2 className="w-5 h-5 text-primary animate-spin" />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-border" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className={`font-medium text-base ${isCompilingReport ? 'text-foreground' : 'text-muted-foreground'}`}>
                  Compiling Report
                </p>
                <p className={`text-sm ${isCompilingReport ? 'text-primary' : 'text-muted-foreground'}`}>
                  {isCompilingReport ? 'Analyzing and preparing your results...' : 'Waiting'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisProgress;
